import React from "react";
import { cn } from "@/lib/utils";
import { statusBadgeMap, badgeToneToClass, humanizeStatus } from "@/lib/brand-tokens";

/**
 * StatusBadge — the only sanctioned way to render a status pill.
 * Resolves the tone from the canonical map; never hand-pick badge colours.
 *
 *   <StatusBadge entity="deal" status="Published" />
 *   <StatusBadge tone="warning">Awaiting</StatusBadge>
 */
// Entity-specific overrides for clearer demo copy. "Pending" alone is
// ambiguous; in card / submission contexts it specifically means awaiting
// admin approval, so we surface that.
const LABEL_OVERRIDES = {
  card:       { Pending: "Pending Approval", FlaggedFormat: "Flagged", FlaggedDuplicate: "Flagged" },
  submission: { Pending: "Pending Approval", PendingReview: "Pending Approval" },
  // Member-facing payment statuses. Admin pages keep the granular labels via
  // entity="payment". The member only cares about user-meaningful states:
  //   Eligible/Scheduled → "Approved for Payment" (admin recognised, no money has moved)
  //   Sent              → "Payment Sent"          (ACH out, 2–4 days to land)
  //   Completed         → "Payment Received"      (funds in bank)
  //   Returned          → "Returned by Bank"
  //   Refunded          → "Refunded"
  //   Cancelled         → "Cancelled"
  memberPayment: {
    Eligible:  "Approved for Payment",
    Scheduled: "Approved for Payment",
    Sent:      "Payment Sent",
    Completed: "Payment Received",
    Returned:  "Returned by Bank",
    Refunded:  "Refunded",
    Cancelled: "Cancelled",
  },
};

export function StatusBadge({ entity, status, tone, dot = false, className, children }) {
  let resolvedTone = tone;
  if (!resolvedTone && entity && status) {
    resolvedTone = statusBadgeMap[entity]?.[status] || "neutral";
  }
  resolvedTone = resolvedTone || "neutral";

  const palette = badgeToneToClass[resolvedTone] || badgeToneToClass.neutral;
  const overrideLabel = entity && status ? LABEL_OVERRIDES[entity]?.[status] : null;
  const label = children || overrideLabel || humanizeStatus(status) || "—";

  return (
    <span className={cn("status-pill border", palette.bg, palette.text, "border-transparent", className)}>
      {dot && <span className={cn("size-1.5 rounded-full", palette.dot)} />}
      {label}
    </span>
  );
}

export default StatusBadge;
